import { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";

function EmployeeWorkQueue() {
  const navigate = useNavigate();

  const [employee, setEmployee] =
    useState(null);

  const [leads, setLeads] =
    useState([]);

  const [selectedDate, setSelectedDate] =
    useState("");

  const [filter, setFilter] =
    useState("All");

  const [loading, setLoading] =
    useState(true);

  const today = new Date()
    .toISOString()
    .split("T")[0];

  useEffect(() => {
    const savedEmployee =
      localStorage.getItem("crmEmployee");

    if (!savedEmployee) {
      navigate("/employee-login");
      return;
    }

    let parsedEmployee;

    try {
      parsedEmployee =
        JSON.parse(savedEmployee);
    } catch (error) {
      localStorage.removeItem(
        "crmEmployee"
      );

      navigate("/employee-login");
      return;
    }

    if (!parsedEmployee?.email) {
      navigate("/employee-login");
      return;
    }

    setEmployee(parsedEmployee);

    const loadLeads = async () => {
      try {
        setLoading(true);

        const leadQuery = query(
          collection(db, "leads"),
          where(
            "assignedEmployeeEmail",
            "==",
            parsedEmployee.email
              .trim()
              .toLowerCase()
          )
        );

        const snapshot =
          await getDocs(leadQuery);

        setLeads(
          snapshot.docs.map(
            (documentItem) => ({
              id: documentItem.id,
              ...documentItem.data(),
            })
          )
        );
      } catch (error) {
        console.error(
          "Work queue loading error:",
          error
        );
      } finally {
        setLoading(false);
      }
    };

    loadLeads();
  }, [navigate]);

  const getTimeValue = (value) => {
    if (!value) {
      return 0;
    }

    try {
      if (
        typeof value.toDate ===
        "function"
      ) {
        return value
          .toDate()
          .getTime();
      }

      const date =
        new Date(value);

      return Number.isNaN(
        date.getTime()
      )
        ? 0
        : date.getTime();
    } catch (error) {
      return 0;
    }
  };

  const getWorkedTime = (
    lead
  ) => {
    /*
      Only actual call/remark activity
      should push a lead downward.

      We intentionally do NOT use updatedAt
      because Admin edits or customer-detail
      edits should not count as worked.
    */

    return getTimeValue(
      lead.lastContactedAt
    );
  };

  const filteredLeads =
    useMemo(() => {
      return leads
        .filter((lead) => {
          if (
            lead.status ===
            "Converted"
          ) {
            return false;
          }

          if (
            lead.status ===
            "Prospect"
          ) {
            return false;
          }

          if (selectedDate) {
            const workDate =
              lead.callbackDate ||
              lead.deadline ||
              "";

            if (
              workDate !==
              selectedDate
            ) {
              return false;
            }
          }

          if (
            filter ===
            "All"
          ) {
            return true;
          }

          if (
            filter ===
            "Overdue"
          ) {
            const date =
              lead.callbackDate ||
              lead.deadline;

            return (
              date &&
              date < today
            );
          }

          if (
            filter ===
            "Not Contacted"
          ) {
            return !lead.lastContactedAt;
          }

          return (
            lead.status ===
              filter ||
            lead.lastDisposition ===
              filter
          );
        })
        .sort((a, b) => {
          const aWorked =
            getWorkedTime(a);

          const bWorked =
            getWorkedTime(b);

          /*
            Fresh / untouched leads
            always stay above worked leads.
          */

          if (
            !aWorked &&
            bWorked
          ) {
            return -1;
          }

          if (
            aWorked &&
            !bWorked
          ) {
            return 1;
          }

          /*
            If both are fresh,
            newest fresh lead first.
          */

          if (
            !aWorked &&
            !bWorked
          ) {
            return (
              getTimeValue(
                b.createdAt
              ) -
              getTimeValue(
                a.createdAt
              )
            );
          }

          /*
            If both are already worked:

            oldest worked lead stays above,
            most recently updated lead
            goes to the very bottom.
          */

          return (
            aWorked -
            bWorked
          );
        });
    }, [
      leads,
      filter,
      selectedDate,
      today,
    ]);

  return (
    <div className="employee-calls-page">

      <header className="mobile-crm-header">

        <div>

          <h1>
            Trading Wealth CRM
          </h1>

          <p>
            Work to Non Work
          </p>

        </div>

        <button
          type="button"
          className="mobile-back-button"
          onClick={() =>
            navigate(
              "/employee-calls"
            )
          }
        >
          My Calls
        </button>

      </header>

      <main className="employee-calls-main">

        <section className="employee-calls-heading">

          <p className="small-label">
            DAILY WORK QUEUE
          </p>

          <h2>
            {employee?.name ||
              "Employee"}
          </h2>

          <p>
            Fresh leads remain at the top.
            Recently worked leads automatically
            move toward the bottom.
          </p>

        </section>

        <div className="form-group">

          <label>
            Select Work Date
          </label>

          <input
            type="date"
            value={
              selectedDate
            }
            onChange={(
              event
            ) =>
              setSelectedDate(
                event.target.value
              )
            }
          />

        </div>

        <div className="employee-filter-tabs">

          {[
            "All",
            "Overdue",
            "Not Contacted",
            "Not Answered",
            "Busy",
            "Ringing",
            "Switched Off",
            "Not Interested",
            "Invalid Number",
          ].map(
            (item) => (

              <button
                key={
                  item
                }
                type="button"
                className={
                  filter ===
                  item
                    ? "employee-filter-button active"
                    : "employee-filter-button"
                }
                onClick={() =>
                  setFilter(
                    item
                  )
                }
              >
                {item}
              </button>

            )
          )}

        </div>

        {loading ? (

          <div className="employee-empty-state">
            Loading work...
          </div>

        ) : filteredLeads.length ===
          0 ? (

          <div className="employee-empty-state">
            No leads found.
          </div>

        ) : (

          <div className="mobile-lead-list">

            {filteredLeads.map(
              (lead) => (

                <article
                  className="mobile-lead-card"
                  key={
                    lead.id
                  }
                >

                  <div className="mobile-lead-card-top">

                    <div>

                      <h3>
                        {lead.customerName ||
                          lead.name ||
                          "Customer"}
                      </h3>

                      <p>
                        {lead.phone ||
                          "-"}
                      </p>

                    </div>

                    <span className="mobile-status pending">
                      {lead.status ||
                        "Pending"}
                    </span>

                  </div>

                  <div className="mobile-lead-meta">

                    <span>
                      Assigned/Deadline:{" "}
                      {lead.deadline ||
                        "-"}
                    </span>

                    <span>
                      Callback:{" "}
                      {lead.callbackDate ||
                        "-"}{" "}
                      {lead.callbackTime ||
                        ""}
                    </span>

                  </div>

                  {lead.lastRemark && (

                    <p className="latest-remark-preview">
                      Latest:{" "}
                      {lead.lastRemark}
                    </p>

                  )}

                  <div className="mobile-lead-actions">

                    <a
                      className="small-call-button"
                      href={`tel:${
                        lead.phone ||
                        ""
                      }`}
                    >
                      Call
                    </a>

                    <button
                      type="button"
                      className="open-lead-button"
                      onClick={() =>
                        navigate(
                          `/employee-calls?lead=${lead.id}`
                        )
                      }
                    >
                      Open & Update
                    </button>

                  </div>

                </article>

              )
            )}

          </div>

        )}

      </main>

    </div>
  );
}

export default EmployeeWorkQueue;